import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import {
  ArrowRight,
  PlayCircle,
  CheckCircle,
  Brain,
  Target,
  Zap,
  Users,
  Sparkles
} from 'lucide-react';

type DemoStep = 'landing' | 'foundations' | 'quality-control' | 'professional-mastery' | 'conclusion';

const DemoExperience: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<DemoStep>('landing');
  const [completedModules, setCompletedModules] = useState<DemoStep[]>([]);

  const markModuleComplete = (module: DemoStep) => {
    if (!completedModules.includes(module)) {
      setCompletedModules(prev => [...prev, module]);
    }
  };

  const getProgressPercent = () => {
    const steps: DemoStep[] = ['foundations', 'quality-control', 'professional-mastery'];
    const completed = steps.filter(step => completedModules.includes(step)).length;
    return (completed / steps.length) * 100;
  };

  if (currentStep === 'landing') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="container mx-auto px-6 py-16 max-w-6xl">
          {/* Logo/Brand Section */}
          <div className="text-center mb-16">
            <div className="w-32 h-32 mx-auto mb-8 bg-white rounded-3xl shadow-lg flex items-center justify-center">
              <SupabaseIcon
                iconPath="lyra-avatar.png"
                alt="Lyra AI Mentor"
                size="xl"
                className="w-20 h-20"
              />
            </div>

            <div className="mb-12">
              <div className="inline-block bg-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
                FOR NON-PROFIT & PUBLIC INTEREST PROFESSIONALS
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Master AI Without the Mystery
              </h1>

              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
                Transform your impact with AI-powered workflows through 40+ hands-on lessons and 135+ professional prompts. Learn practical tools quickly, navigate complex ethics confidently, and amplify your mission with bite-sized training that fits your schedule - designed specifically for busy nonprofit and public interest professionals.
              </p>

              <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-12">
                From fundraising and donor communications to program management and data storytelling - master the AI skills that matter most to your work without disrupting your daily responsibilities. All with your personal AI mentor, Lyra, providing guidance, feedback, and support at your own pace.
              </p>

              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => setCurrentStep('foundations')}
              >
                Start Your AI Journey
              </Button>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="learning-target.png"
                  alt="Tailored Learning"
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Tailored Learning</h3>
              <p className="text-gray-600 leading-relaxed">
                Personalized to your role, tech comfort, and learning style
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="mission-heart.png"
                  alt="Mission-Focused"
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Mission-Focused</h3>
              <p className="text-gray-600 leading-relaxed">
                Practical examples from fundraising to program management
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="achievement-trophy.png"
                  alt="Confidence Building"
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Confidence Building</h3>
              <p className="text-gray-600 leading-relaxed">
                Build expertise through real AI-powered lessons with clear progress
              </p>
            </div>
          </div>

          {/* Welcome Message - Lyra */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-16 max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-2xl bg-cyan-500 mx-auto mb-4 flex items-center justify-center shadow-md">
                <div className="text-white text-2xl">🤖</div>
              </div>
              <h2 className="text-2xl font-bold text-purple-600 mb-4">Welcome Back Greg</h2>
            </div>
            <div className="text-center">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                "I'm Lyra, your AI expert coach. I'll guide you through this masterclass experience, connecting you with real professionals like Maya who have transformed their work through systematic AI mastery. Ready to see how we move organizations from AI overwhelm to AI leadership?"
              </p>
              <div className="bg-purple-50 px-6 py-4 rounded-xl border border-purple-100">
                <strong className="text-purple-800">Your 15-Minute Journey:</strong>
                <div className="text-purple-700 mt-2">Foundations → Quality Control → Professional Transformation</div>
              </div>
            </div>
          </div>

          {/* Demo Modules */}
          <div className="space-y-12">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Your Learning Journey
            </h2>
            <p className="text-center text-gray-600 text-lg mb-12 max-w-2xl mx-auto">
              Three focused chapters designed to take you from AI curious to AI confident
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Module 1 */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setCurrentStep('foundations')}>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md mx-auto mb-6 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="lyra-avatar.png"
                      alt="Lyra"
                      size="lg"
                      className="w-12 h-12"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Chapter 1: Meet Lyra, Your AI Learning Companion</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Discover how Lyra, your AI mentor, can transform your nonprofit work through personalized guidance and real-time assistance
                  </p>
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl shadow-md">
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Start Chapter
                </Button>
              </div>

              {/* Module 2 */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setCurrentStep('quality-control')}>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md mx-auto mb-6 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="communication.png"
                      alt="Maya"
                      size="lg"
                      className="w-12 h-12"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Chapter 2: Maya's Communication Mastery</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Master professional email communication with Maya Rodriguez as she transforms her daily work through AI-enhanced writing, tone optimization, and template creation for maximum impact.
                  </p>
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl shadow-md">
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Start Chapter
                </Button>
              </div>

              {/* Module 3 */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setCurrentStep('professional-mastery')}>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md mx-auto mb-6 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="data-analytics.png"
                      alt="Sofia"
                      size="lg"
                      className="w-12 h-12"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Chapter 3: Sofia's Storytelling & Project Management</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Master AI-enhanced storytelling with Sofia to create compelling narratives that engage donors, inspire volunteers, and amplify your mission's impact.
                  </p>
                </div>
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl shadow-md">
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Start Chapter
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  if (currentStep === 'foundations') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Progress Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => setCurrentStep('landing')}>
                ← Back to Overview
              </Button>
              <div className="text-sm text-gray-600">Module 1 of 3</div>
            </div>
            <Progress value={33} className="h-2 mb-2" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">AI Foundations with Lyra</h2>
              <p className="text-cyan-600">Building your AI confidence</p>
            </div>
          </div>

          {/* Lyra Introduction */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="lyra-avatar.png"
                  alt="Lyra"
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Lyra's AI Foundations</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  "Welcome! I'm Lyra, your AI expert coach. Before you meet Maya and dive into professional applications, let's build your AI confidence with essential foundations. Think of this as your AI literacy bootcamp."
                </p>
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
                  <div className="text-purple-800 font-semibold">What You'll Master:</div>
                  <div className="text-purple-700 mt-2">AI basics → Smart prompting → Tool selection → Ethics awareness</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Foundations Experience */}
          <div className="space-y-8">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick AI Foundation Check</h3>
              <p className="text-gray-600 mb-6">Let's see what you already know and fill in the gaps</p>

              <div className="bg-gray-50 p-6 rounded-2xl mb-6">
                <p className="text-gray-700">
                  <strong>Scenario:</strong> You need to write a donor thank you email. Which approach shows better AI mastery?
                </p>
              </div>

              <div className="space-y-4">
                <button
                  className="w-full p-6 text-left bg-white rounded-2xl shadow-md hover:shadow-lg transition-all border-2 border-transparent hover:border-red-200"
                  onClick={() => {
                    markModuleComplete('foundations');
                    setCurrentStep('quality-control');
                  }}
                >
                  <strong className="text-red-600">Approach A:</strong> "Write a donor thank you email"
                </button>
                <button
                  className="w-full p-6 text-left bg-white rounded-2xl shadow-md hover:shadow-lg transition-all border-2 border-transparent hover:border-green-200"
                  onClick={() => {
                    markModuleComplete('foundations');
                    setCurrentStep('quality-control');
                  }}
                >
                  <strong className="text-green-600">Approach B:</strong> "You are Maya Rodriguez, Program Coordinator at Hope Gardens Community Center. Write a thank you email to Sarah, our monthly donor, for her $75 gift. Include specific impact: Marcus improved from grade 1 to grade 3 reading level in 4 months thanks to tutoring your support funds. Keep tone warm but professional, under 150 words."
                </button>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-8 max-w-4xl mx-auto">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <SupabaseIcon
                    iconPath="lyra-avatar.png"
                    alt="Lyra"
                    size="md"
                    className="w-8 h-8"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 mb-2">Lyra's Foundation Insight</h4>
                  <p className="text-gray-700">
                    "The difference between these approaches is the difference between AI user and AI director. Approach B shows systematic AI briefing - you're giving AI context, persona, specific details, tone guidance, and constraints. This is the foundation of professional AI mastery that Maya will demonstrate next."
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => setCurrentStep('quality-control')}
              size="lg"
              className="bg-purple-600 hover:bg-purple-700"
            >
              Continue to Maya's Quality Control System →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'quality-control') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Progress Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => setCurrentStep('foundations')}>
                ← Previous Module
              </Button>
              <div className="text-sm text-gray-600">Module 2 of 3</div>
            </div>
            <Progress value={66} className="h-2 mb-2" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Maya's Quality Control System</h2>
              <p className="text-purple-600">Spot and fix AI mistakes like a pro</p>
            </div>
          </div>

          {/* Maya Introduction */}
          <Card className="mb-6 border-purple-200 bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                  M
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">Maya Rodriguez, Program Coordinator - Hope Gardens Community Center</h3>
                  <p className="text-gray-700 mb-3">
                    "Hi! I'm Maya, and six months ago I was drowning in AI-generated content that sounded corporate and wrong for our community center. Now I can spot AI mistakes instantly and fix them in minutes. Let me show you my quality control system."
                  </p>
                  <div className="bg-purple-100 p-3 rounded">
                    <strong className="text-purple-800">Maya's Challenge:</strong>
                    <span className="text-purple-700 ml-2">Transform generic AI → Mission-aligned communication</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Quality Demo */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">Before: Generic AI Output</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-700 bg-white p-3 rounded border">
                  "Dear Valued Supporter,<br/><br/>
                  Thank you for your generous contribution to our organization. Your support enables us to continue our important work in the community.<br/><br/>
                  We appreciate your ongoing partnership.<br/><br/>
                  Sincerely, The Team"
                </div>
                <div className="mt-3 text-xs text-red-600">
                  ❌ Generic • ❌ No impact • ❌ Corporate tone
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-700">After: Mission-Aligned AI</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-700 bg-white p-3 rounded border">
                  "Dear Sarah,<br/><br/>
                  Your $75 monthly gift directly funds Marcus's transformation! This 9-year-old advanced from grade 1 to grade 3 reading level in 4 months.<br/><br/>
                  Would you like to visit our Tuesday tutoring session?<br/><br/>
                  With gratitude, Maya Rodriguez"
                </div>
                <div className="mt-3 text-xs text-green-600">
                  ✅ Personal • ✅ Specific impact • ✅ Authentic voice
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Interactive Element */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Maya's Spot the Problem Challenge</CardTitle>
              <CardDescription>Click on what's wrong with this AI output</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded border mb-4">
                <p className="text-gray-700">
                  "Dear Supporter, Hope Gardens serves <span className="bg-yellow-200 px-1 cursor-pointer hover:bg-yellow-300" title="Click to spot the error">300 families monthly</span> since our founding in <span className="bg-yellow-200 px-1 cursor-pointer hover:bg-yellow-300" title="Click to spot the error">2015</span>. Our <span className="bg-yellow-200 px-1 cursor-pointer hover:bg-yellow-300" title="Click to spot the error">Program Director</span> thanks you."
                </p>
              </div>
              <div className="text-sm text-purple-700">
                💡 <strong>Maya's Insight:</strong> "AI invented facts I didn't give it! We serve 120 families (not 300), founded in 2018 (not 2015), and I'm Program Coordinator (not Director). This is why I systematically fact-check every AI output."
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button
              onClick={() => {
                markModuleComplete('quality-control');
                setCurrentStep('professional-mastery');
              }}
              size="lg"
              className="bg-pink-600 hover:bg-pink-700"
            >
              Continue to Professional AI Infrastructure →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'professional-mastery') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Progress Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => setCurrentStep('quality-control')}>
                ← Previous Module
              </Button>
              <div className="text-sm text-gray-600">Module 3 of 3</div>
            </div>
            <Progress value={100} className="h-2 mb-2" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Professional AI Infrastructure</h2>
              <p className="text-pink-600">Maya's strategic campaign mastery</p>
            </div>
          </div>

          {/* Maya's Professional Challenge */}
          <Card className="mb-6 border-pink-200 bg-pink-50">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold">
                  M
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">Maya's Real Challenge: Program Expansion</h3>
                  <p className="text-gray-700 mb-3">
                    "I need to expand Hope Gardens after-school program from 40 to 55 students. That means convincing parents, school district, city council, and donors - each with different concerns. Watch how I use AI strategically to build coordinated campaigns."
                  </p>
                  <div className="bg-pink-100 p-3 rounded">
                    <strong className="text-pink-800">Professional Challenge:</strong>
                    <span className="text-pink-700 ml-2">$18K funding • Multiple stakeholders • Strategic coordination required</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategic Brief Demo */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Maya's Strategic Brief</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <div className="space-y-2">
                    <div><strong>Goal:</strong> Secure support for serving 15 more students</div>
                    <div><strong>Audience:</strong> School district (data-focused)</div>
                    <div><strong>Key Message:</strong> Proven 1:8 ratio maintained</div>
                    <div><strong>Context:</strong> 2+ years success, waiting list of 15</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI-Generated Content</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 p-3 rounded text-sm text-green-800">
                  "Superintendent Williams,<br/><br/>
                  Hope Gardens requests partnership expansion. Our proven 1:8 student-to-staff ratio ensures quality as we serve 15 additional students from our verified waiting list.<br/><br/>
                  Data shows 2+ years of academic improvement outcomes.<br/><br/>
                  Maya Rodriguez, Program Coordinator"
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transformation Showcase */}
          <Card className="mb-6 bg-pink-50 border-pink-200 shadow-lg">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Maya's Transformation</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600 mb-2">Before</div>
                    <div className="text-gray-600 text-sm space-y-1">
                      <div>2 hours per email</div>
                      <div>Reactive communication</div>
                      <div>Generic AI outputs</div>
                      <div>Overwhelming workload</div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">After</div>
                    <div className="text-gray-600 text-sm space-y-1">
                      <div>10 minutes per campaign</div>
                      <div>Strategic AI direction</div>
                      <div>Mission-aligned content</div>
                      <div>Systematic workflow</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button
              onClick={() => {
                markModuleComplete('professional-mastery');
                setCurrentStep('conclusion');
              }}
              size="lg"
              className="bg-pink-600 hover:bg-pink-700 text-white shadow-md"
            >
              See Your Complete AI Mastery Path →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'conclusion') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-cyan-500 mx-auto mb-4 flex items-center justify-center shadow-md">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Congratulations! You've Experienced AI Mastery
            </h2>
            <p className="text-xl text-gray-600">
              You've seen the complete journey from AI foundations to professional transformation
            </p>
          </div>

          {/* Journey Recap */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-center">Your AI Mastery Journey</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 text-white flex items-center justify-center font-bold text-sm">L</div>
                  <div className="flex-1">
                    <strong>Lyra's Foundations:</strong> You learned the difference between AI user and AI director through systematic briefing
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-sm">M</div>
                  <div className="flex-1">
                    <strong>Maya's Quality Control:</strong> You experienced hallucination detection and mission alignment techniques
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold text-sm">M</div>
                  <div className="flex-1">
                    <strong>Professional Infrastructure:</strong> You saw strategic campaign building and systematic AI workflows
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="text-center space-y-6">
            <h3 className="text-2xl font-semibold text-gray-900">Ready for Complete AI Mastery?</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              This was just a taste of the complete Lyra AI Mentor experience. Join Maya, Lyra, and other professional characters for the full transformation journey that builds your organization's complete AI infrastructure.
            </p>

            <div className="space-y-4">
              <Button
                size="lg"
                className="bg-cyan-600 hover:bg-cyan-700 text-white shadow-md text-lg px-8 py-3"
                onClick={() => window.open('https://www.hellolyra.app', '_blank')}
              >
                <Users className="w-5 h-5 mr-2" />
                Join Maya's Complete Journey
              </Button>

              <div className="text-sm text-gray-500">
                Full platform • Multiple character journeys • Complete professional transformation
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DemoExperience;